import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OnboardingStep1Welcome from '../components/onboarding/OnboardingStep1Welcome';
import OnboardingStep2Church from '../components/onboarding/OnboardingStep2Church';
import OnboardingStep3Preferences from '../components/onboarding/OnboardingStep3Preferences';
import OnboardingStep4Family from '../components/onboarding/OnboardingStep4Family';
import OnboardingStep5Readers from '../components/onboarding/OnboardingStep5Readers';
import OnboardingStep6Complete from '../components/onboarding/OnboardingStep6Complete';
import LoadingSpinner from '../components/LoadingSpinner';
import './OnboardingPage.css';

const STEP_COMPONENTS = {
  1: OnboardingStep1Welcome,
  2: OnboardingStep2Church,
  3: OnboardingStep3Preferences,
  4: OnboardingStep4Family,
  5: OnboardingStep5Readers,
  6: OnboardingStep6Complete,
};

const STEP_TITLES = {
  1: 'Welcome to ReadTheWord',
  2: 'Connect with Your Church',
  3: 'Set Your Preferences',
  4: 'Create or Join a Family',
  5: 'Add Family Readers',
  6: 'You\'re All Set!',
};

const STEP_DESCRIPTIONS = {
  1: 'Let\'s get you started with your Bible reading journey',
  2: 'Connect with your church to join their reading community',
  3: 'Customize your reading experience',
  4: 'Families allow you to track reading progress together',
  5: 'Add family members to your reading group',
  6: 'You\'re ready to start reading!',
};

const OnboardingPage = () => {
  const { onboarding, advanceOnboarding, completeOnboarding } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [stepData, setStepData] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (onboarding?.currentStep) {
      setCurrentStep(onboarding.currentStep);
    }
  }, [onboarding]);

  // Redirect to home if onboarding is already completed
  useEffect(() => {
    if (onboarding?.completed) {
      navigate('/');
    }
  }, [onboarding, navigate]);

  const handleNext = async (data = null, skipStep = false) => {
    setIsLoading(true);
    try {
      console.log('🔍 OnboardingPage handleNext called with data:', data);
      
      const result = await advanceOnboarding(skipStep);
      setCurrentStep(result.onboarding.currentStep);
      
      if (data) {
        setStepData(data);
      }
      
      // Handle step-specific actions
      if (data?.action) {
        console.log('🔍 OnboardingPage handling action:', data.action);
        switch (data.action) {
          case 'create_family':
            // Family was created successfully, continue to next step
            console.log('🔍 Family created successfully, continuing onboarding');
            break;
          case 'join_family':
            // Family was joined successfully, continue to next step
            console.log('🔍 Family joined successfully, continuing onboarding');
            break;
          case 'add_readers':
            // Readers were added successfully, continue to next step
            console.log('🔍 Readers added successfully, continuing onboarding');
            break;
          default:
            console.log('🔍 Unknown action:', data.action);
        }
      }
      
      // If onboarding is completed, redirect to home
      if (result.onboarding.completed) {
        console.log('🔍 Onboarding completed, redirecting to home');
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to advance onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      await completeOnboarding();
      navigate('/');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipAll = async () => {
    setIsLoading(true);
    try {
      await completeOnboarding();
      navigate('/');
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const StepComponent = STEP_COMPONENTS[currentStep];

  if (!StepComponent) {
    return (
      <div className="onboarding-page">
        <LoadingSpinner size="large" message="Loading onboarding..." />
      </div>
    );
  }

  const progressPercentage = ((currentStep - 1) / 5) * 100;

  return (
    <div className="onboarding-page">
      {/* Header */}
      <div className="onboarding-header">
        <div className="onboarding-logo">
          <img src="/logo.jpeg" alt="ReadTheWord Logo" className="onboarding-app-logo" />
          <h1>ReadTheWord</h1>
        </div>
        
        <div className="onboarding-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <span className="progress-text">Step {currentStep} of 6</span>
        </div>

        <button 
          className="onboarding-skip" 
          onClick={handleSkipAll}
          disabled={isLoading}
        >
          Skip All
        </button>
      </div>

      {/* Main Content */}
      <div className="onboarding-main">
        <div className="onboarding-content">
          {/* Step Title and Description */}
          <div className="step-header">
            <h2>{STEP_TITLES[currentStep]}</h2>
            <p>{STEP_DESCRIPTIONS[currentStep]}</p>
          </div>

          {/* Step Component */}
          <div className="step-content">
            <StepComponent 
              onNext={handleNext}
              onComplete={handleComplete}
              onSkip={() => handleNext(null, true)}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              stepData={stepData}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="onboarding-footer">
        <div className="step-indicators">
          {[1, 2, 3, 4, 5, 6].map(step => (
            <div 
              key={step}
              className={`step-indicator ${step <= currentStep ? 'active' : ''} ${step === currentStep ? 'current' : ''}`}
              onClick={() => step < currentStep && setCurrentStep(step)}
            />
          ))}
        </div>
        
        <div className="onboarding-help">
          <p>Need help? Contact your church administrator or support team.</p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
